import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Date adapter for Neo.One Material datepickers (NEO-3441).
 *
 * Extends the **native** adapter so datepicker `FormControl` values stay JS
 * `Date` objects (no app-wide Date→Moment ripple). It overrides ONLY:
 *  - `parse()`  — so **manual keyboard input** is read strictly as `DD/MM/YYYY`
 *                 (accepts 1- or 2-digit day/month; rejects other formats →
 *                 the field flags `matDatepickerParse`). The native adapter
 *                 otherwise parses with `Date()` which reads month-first.
 *  - `format()` — so dates display as `DD/MM/YYYY`.
 *
 * Picker selection, value type, and everything else are inherited unchanged —
 * only typed input behaviour is affected.
 */
@Injectable()
export class NeoDateAdapter extends NativeDateAdapter {
  override parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!match) {
        return this.invalid();
      }
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      if (month < 1 || month > 12 || day < 1 || day > 31) {
        return this.invalid();
      }
      const date = new Date(year, month - 1, day);
      date.setHours(0, 0, 0, 0);
      // Reject calendar overflow (e.g. 31/02/2026 → 03/03).
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return this.invalid();
      }
      return date;
    }
    return super.parse(value as never);
  }

  override format(date: Date, displayFormat: unknown): string {
    // Only override the field/input display (formats that include a day, i.e.
    // dateInput / dateA11yLabel) → DD/MM/YYYY. Month/year labels (calendar
    // header) have no `day` and are left to the native Intl formatter.
    const isDayFormat = !!(displayFormat as Intl.DateTimeFormatOptions)?.day;
    if (this.isValid(date) && isDayFormat) {
      const day = String(this.getDate(date)).padStart(2, '0');
      const month = String(this.getMonth(date) + 1).padStart(2, '0');
      const year = this.getYear(date);
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat as object);
  }
}
